// v4 ticket V4-MC (spec-v4-models section 1): compare models on a scene. The pure side: reading a comparison from the
// API (normComparison), the live comparison behind a stage item, what the Director hears after a tap, and the line-up
// helpers the "Try on other models" sheet uses. No imports from api.ts (api.ts imports normComparison from here); the
// calls are in compareClient.ts.
import type { CompareCandidate, Comparison, Project, StageCompare } from "./types";

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const s = (v: unknown) => (typeof v === "string" ? v : "");
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const LETTERS = ["A", "B", "C", "D"] as const;

export const candidateLabel = (short: string, credits: number) => `${short} · ${credits} credit${credits === 1 ? "" : "s"}`;

export function normCandidate(v: unknown, i: number): CompareCandidate {
  const o = isObj(v) ? v : {};
  const short = s(o.short) || s(o.model);
  const credits = n(o.credits);
  const status = o.status === "ready" || o.status === "failed" ? o.status : "working";
  return {
    index: i,
    letter: LETTERS[i] ?? "A",
    model: s(o.model),
    short,
    credits,
    ...(n(o.durationS) ? { durationS: n(o.durationS) } : {}),
    jobId: s(o.jobId) || null,
    assetId: s(o.assetId) || null,
    url: s(o.url) || null,
    status: status === "ready" && !s(o.url) ? "working" : status,
    ...(o.error ? { error: s(o.error) } : {}),
    label: s(o.label) || candidateLabel(short, credits),
  };
}

export function normComparison(v: unknown): Comparison {
  const o = isObj(v) ? v : {};
  return {
    id: s(o.id),
    step: o.step === "frame" ? "frame" : "clip",
    at: s(o.at),
    pickedModel: s(o.pickedModel) || null,
    pickedAssetId: s(o.pickedAssetId) || null,
    ...(o.fromFrameUrl ? { fromFrameUrl: s(o.fromFrameUrl) } : {}),
    candidates: (Array.isArray(o.candidates) ? o.candidates : []).slice(0, 4).map(normCandidate),
  };
}

/** The comparison a stage item names, from the project as it is now (candidates land one by one), or null. */
export function liveComparison(p: Pick<Project, "shots"> | null | undefined, c: Pick<StageCompare, "sceneId" | "id">): Comparison | null {
  return p?.shots[c.sceneId]?.comparisons?.find((x) => x.id === c.id) ?? null;
}

/** The scene's newest comparison, if any. */
export const latestComparison = (p: Pick<Project, "shots">, sceneId: string): Comparison | null => p.shots[sceneId]?.comparisons?.at(-1) ?? null;

/** "2 of 3 ready", "All 3 ready", "Making 3" */
export function progressWords(c: Pick<Comparison, "candidates">): string {
  const all = c.candidates.length;
  const ready = c.candidates.filter((x) => x.status === "ready").length;
  const failed = c.candidates.filter((x) => x.status === "failed").length;
  if (ready === all) return `All ${all} ready`;
  if (ready + failed === all) return `${ready} of ${all} ready, ${failed} didn't work (refunded)`;
  return ready ? `${ready} of ${all} ready` : `Making ${all}`;
}

export type CompareAction = "use" | "again" | "default";

/** What the Director hears after a tap (an [app] user message, like a Pick on the stage). */
export function compareLine(action: CompareAction, c: CompareCandidate, step: "frame" | "clip", sceneN: number | null, scope?: "video" | "series"): string {
  const where = sceneN ? `scene ${sceneN}'s ${step}` : `the ${step}`;
  if (action === "use") return `[app] The user used ${c.letter} (${c.short}) for ${where} by tapping.`;
  if (action === "again") return `[app] The user started another ${step} with ${c.short} for ${sceneN ? `scene ${sceneN}` : "the scene"} by tapping (${c.credits} credits).`;
  return `[app] The user made ${c.short} the default ${step === "clip" ? "video" : "picture"} model for ${scope === "series" ? "the series" : "this video"} by tapping.`;
}

/** The comparison's total for a line-up (what the Start button says). */
export const lineupTotal = (xs: { credits: number }[]) => xs.reduce((a, x) => a + x.credits, 0);

/** Toggles a model in a line-up, keeping 2-4 (a fifth tap replaces the oldest pick). */
export function toggleModel(picked: string[], id: string, max = 4): string[] {
  if (picked.includes(id)) return picked.filter((x) => x !== id);
  const next = [...picked, id];
  return next.length > max ? next.slice(next.length - max) : next;
}

/** Synced playback of 2-4 clips: all from the start together, sound only on the focused one. */
export function syncPlay(videos: (HTMLVideoElement | null)[], focus: number): void {
  videos.forEach((v, i) => {
    if (!v) return;
    v.currentTime = 0;
    v.muted = i !== focus;
    void v.play().catch(() => {
      v.muted = true; // a browser that blocks sound on one: play it muted rather than not at all
      void v.play().catch(() => {});
    });
  });
}
